"use client";
// Imports
import { useEffect, useState } from "react";
import { ChevronDown, Footprints, X } from "lucide-react";
import { getEventsByDay } from "@/lib/helpersTwo";
import { mapButtonStyles } from "@/lib/utils";

import { motion } from "framer-motion";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { EventTimes } from "@/components";
import { getDatesEventIsOn, getEventTimesByDay } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export function MapPopout({
  mapRef,
  zoomAwaySelectedLoc,
  selectedMarker,
  daysOfShow,
  showDates,
  isModalOpen,
}) {
  const [selectedDay, setSelectedDay] = useState({
    slug: "all-days",
    label: "All Events",
  });

  const [eventsModalOpen, setEventsModalOpen] = useState(false);

  const isAFacility = selectedMarker?.location?.facilities?.length > 0;
  const lat = isAFacility
    ? selectedMarker?.location?.facilities[0].lat
    : selectedMarker?.location?.lat;
  const long = isAFacility
    ? selectedMarker?.location?.facilities[0].long
    : selectedMarker?.location?.long;

  if (isAFacility) {
    const title =
      selectedMarker?.location?.facilities.length > 0
        ? "Amenities"
        : selectedMarker?.location?.title;

    return (
      <div
        className={`absolute flex flex-col justify-between lg:p-8 p-4 transition-all duration-500 bottom-0 space-y-8 left-0 right-0 w-full h-full bg-brand-wool ${
          selectedMarker && isModalOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="grid items-center grid-cols-4">
          <div className="col-span-3 level-2">
            <h2>{title}</h2>
          </div>

          <div className="flex justify-end">
            <button
              onClick={zoomAwaySelectedLoc}
              className="flex items-center justify-center w-8 h-8 rounded-full bg-foreground text-background"
            >
              <X />
            </button>
          </div>
        </div>
        <div className="flex-1">
          {selectedMarker.location?.facilities?.map((facility, index) => (
            <div key={index} className="w-full mx-auto max-w-8xl">
              <div className="w-full h-full max-w-lg col-span-1 pointer-events-auto">
                <div className="flex flex-col justify-between h-full space-y-4">
                  <div className={facility.description ? "py-4" : ""}>
                    <h2 className="level-3">{facility.title}</h2>
                    <p>{facility.description}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <GetDirectionsButton mapRef={mapRef} lat={lat} long={long} />
      </div>
    );
  }

  return (
    <div
      className={`absolute flex flex-col justify-between transition-all duration-500 bottom-0 lg:p-8 p-4 space-y-4 left-0 right-0 w-full h-full bg-brand-wool ${
        selectedMarker && isModalOpen ? "translate-y-0" : "-translate-x-full"
      }`}
    >
      <div className="grid items-center grid-cols-4">
        <h2 className="col-span-3 level-2">
          {selectedMarker?.location?.title}
        </h2>
        <div className="flex justify-end">
          <button
            onClick={zoomAwaySelectedLoc}
            className="flex items-center justify-center w-8 h-8 rounded-full bg-foreground text-background"
          >
            <X />
          </button>
        </div>
      </div>
      <GetDirectionsButton mapRef={mapRef} lat={lat} long={long} />
      <div className="flex-1 h-full">
        <div className="flex flex-col justify-start h-full space-y-4 overflow-y-scroll">
          {selectedMarker?.location?.description ? (
            <p className="font-body">{selectedMarker?.location?.description}</p>
          ) : null}
          <div className="pb-12 space-y-2">
            {selectedMarker?.location?.events?.length > 0 && (
              <div className="pointer-events-none">
                <Select
                  className="pointer-events-auto"
                  defaultValue="all-days"
                  onValueChange={(value) =>
                    value == "all-days"
                      ? setSelectedDay({ slug: "all-days", label: "All Events" })
                      : setSelectedDay(value)
                  }
                >
                  <SelectTrigger className="w-full h-11 px-3 pointer-events-auto bg-brand-red-gum text-white hover:bg-brand-red-gum">
                    <SelectValue placeholder="Day" />
                    <ChevronDown />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all-days" type="submit">
                      <span>All Events</span>
                    </SelectItem>
                    {daysOfShow?.map((day, index) => {
                      // console.log(day);
                      return (
                        <SelectItem key={index} value={day.value} type="submit">
                          <span>{day.label.day} </span>
                          <span>{day.label.date}</span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}
            <EventsSchedule
              events={getEventsByDay(
                selectedMarker?.location?.events,
                selectedDay,
                showDates
              )}
              selectedDay={selectedDay}
              showDates={showDates}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function EventsSchedule({ events, selectedDay, showDates }) {
  if (!events || events?.length < 1) return null;

  return (
    <div className="flex-1 pb-12 remove-scrollbars">
      <Accordion
        type="single"
        collapsible
        className="flex flex-col w-full space-y-2"
      >
        {events.map((event, index) => (
          <EventsItem
            key={index}
            event={event}
            selectedDay={selectedDay}
            showDates={showDates}
          />
        ))}
      </Accordion>
    </div>
  );
}

function EventsItem({ event, selectedDay, showDates }) {
  const dates = getDatesEventIsOn(event, showDates);

  const eventTimes = getEventTimesByDay(dates, selectedDay);

  return (
    <AccordionItem
      value={event?.id}
      className="text-left border border-grey rounded-base"
    >
      <AccordionTrigger className="p-3 level-5 font-headng">
        <span className="text-left">{event?.title}</span>
      </AccordionTrigger>
      <AccordionContent>
        <div className="px-3 font-body">
          <EventTimes
            eventTimes={eventTimes}
            selectedDay={selectedDay}
            collectionHandle="events"
            everyDay={event?.every_day}
            hideTimesOnCard={event?.hide_times_on_card}
          />
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

function GetDirectionsButton({ mapRef, lat, long }) {
  function drawLineOnMap() {
    if (!mapRef || !mapRef.current) {
      console.error("mapRef is not defined or not initialized");
      return;
    }

    const map = mapRef.current.getMap();
    if (!map) {
      console.error("Map instance is not available");
      return;
    }

    // const startLatLng = ["138.58654625942012", "-34.943970376542545"];

    // get the current location of the user's geo location
    const startLatLng = [currentLocation.lng, currentLocation.lat];
    const endLatLng = [long, lat];

    if (!lat || !long) {
      console.error("Latitude and longitude for the end point are not defined");
      return;
    }

    const line = {
      type: "Feature",
      properties: {},
      geometry: {
        type: "LineString",
        coordinates: [startLatLng, endLatLng],
      },
    };

    if (map.getSource("route")) {
      map.getSource("route").setData(line);
    } else {
      map.addSource("route", {
        type: "geojson",
        data: line,
      });

      map.addLayer({
        id: "route",
        type: "line",
        source: "route",
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "#F0ff00",
          "line-width": 8,
        },
      });
    }

    console.log("Line drawn on map from", startLatLng, "to", endLatLng);
  }

  // return (
  //   <Button
  //     onClick={() => drawLineOnMap()}
  //     size="sm"
  //     className="rounded-full font-body"
  //     variant="outline"
  //   >
  //     <div className="flex items-center space-x-2">
  //       <span className="text-base font-body">Get Directions</span>
  //       <Footprints />
  //     </div>
  //   </Button>
  // );

  return (
    <a
      className="flex items-center justify-center w-full"
      href={`https://www.google.com/maps/dir/?api=1&travelmode=walking&destination=${lat},${long}`}
      target="_blank"
      rel="noreferrer"
    >
      <Button
        size="sm"
        className="rounded-full font-body"
        variant="outline"
        customColors={{
          background: "#892102",
          foreground: "#FFFFFF",
        }}
      >
        <div className="flex items-center space-x-2">
          <span className="text-base font-body">Get Directions</span>
          <Footprints />
        </div>
      </Button>
    </a>
  );
}
